import React, { useMemo, useState } from "react";
import { DataTable } from "ui-components/data-table";
import Button, { ButtonSizes, ButtonThemes } from "ui-components/button/button";
import { XGSSelectOption } from "ui-components/xgs-select/xgsSelect";
import { LabelModes } from "ui-components/molecules/labeled-inputs/labeledInput";
import LabeledTextInput from "ui-components/molecules/labeled-inputs/labeled-text-input/labeledTextInput";
import LabeledSelectInput from "ui-components/molecules/labeled-inputs/labeled-select-input/labeledSelectInput";
import XGSErrorMessage from "ui-components/error-message/errorMessage";
import Loading from "ui-components/loading/loading";
import { DockEmployee } from "app/data/dock-scan/models";
import {
  useDockActiveSessions,
  useDockEmployees,
  useDockLastLogins,
  useDockTags,
} from "../hooks/useDockScanQueries";
import { enrichDockEmployees } from "../dockEmployeeRowTypes";
import {
  DOCK_EMPLOYEE_COLUMN_OPTIONS,
  DOCK_EMPLOYEE_COLUMNS_HIDDEN_BY_DEFAULT,
  getDockEmployeeColumns,
} from "../dockUsersColumns";
import { DOCK_SELECT_MENU_PORTAL } from "../selectMenuPortal";
import { toTagSelectOptions } from "./DockEmployeeTagSelect";

interface DockEmployeesTabProps {
  terminalNumber: number;
  terminalName: string;
  onSelectEmployee: (employee: DockEmployee) => void;
  onCreateEmployee: () => void;
}

const STATUS_OPTIONS: XGSSelectOption[] = [
  { value: "", label: "All Account Statuses" },
  { value: "ACTIVE", label: "Active" },
  { value: "INACTIVE", label: "Inactive" },
];

const DockEmployeesTab: React.FC<DockEmployeesTabProps> = ({
  terminalNumber,
  terminalName,
  onSelectEmployee,
  onCreateEmployee,
}) => {
  const [statusFilter, setStatusFilter] = useState<XGSSelectOption>(STATUS_OPTIONS[0]);
  const [tagFilter, setTagFilter] = useState<XGSSelectOption[]>([]);
  const [search, setSearch] = useState("");
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>(() => {
    const hidden = new Set(DOCK_EMPLOYEE_COLUMNS_HIDDEN_BY_DEFAULT);
    return DOCK_EMPLOYEE_COLUMN_OPTIONS.reduce(
      (acc, { id }) => ({ ...acc, [id]: !hidden.has(id) }),
      {} as Record<string, boolean>
    );
  });
  const [columnOrder, setColumnOrder] = useState<string[]>(() =>
    DOCK_EMPLOYEE_COLUMN_OPTIONS.map(({ id }) => id)
  );

  const status = statusFilter.value as "" | "ACTIVE" | "INACTIVE";
  const tagIds = useMemo(() => tagFilter.map((t) => t.value), [tagFilter]);
  const { data: tags } = useDockTags(terminalNumber);
  const {
    data: employeesData,
    isLoading,
    isError,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useDockEmployees(
    terminalNumber,
    status || undefined,
    tagIds.length > 0 ? tagIds : undefined
  );
  const { data: lastLogins } = useDockLastLogins(terminalNumber);
  const { data: activeSessions } = useDockActiveSessions(terminalNumber);

  const tagOptions = useMemo(() => toTagSelectOptions(tags || []), [tags]);

  const employees = useMemo(
    () =>
      employeesData?.pages.flatMap((page) =>
        Array.isArray(page) ? page : page?.items ?? []
      ) ?? [],
    [employeesData]
  );

  const enrichedEmployees = useMemo(() => {
    const rows = enrichDockEmployees(employees, lastLogins || [], activeSessions || []);
    if (!search.trim()) return rows;
    const q = search.toLowerCase();
    return rows.filter(
      (emp) =>
        emp.employeeId.toLowerCase().includes(q) ||
        emp.firstName.toLowerCase().includes(q) ||
        emp.lastName.toLowerCase().includes(q) ||
        emp.email.toLowerCase().includes(q)
    );
  }, [employees, lastLogins, activeSessions, search]);

  const columns = useMemo(() => getDockEmployeeColumns(), []);

  return (
    <div className="dock-users__tab-panel">
      <div className="dock-users__toolbar">
        <div className="dock-users__filters">
          <LabeledTextInput
            name="search"
            labelMode={LabelModes.column}
            label="Search"
            className="dock-users__filter-field dock-users__filter-field--search"
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            placeholder="Employee ID, name, or email"
            clearable
            onClear={() => setSearch("")}
          />
          <div className="dock-users__filter-field dock-users__filter-field--select">
            <LabeledSelectInput
              name="status"
              labelMode={LabelModes.column}
              label="Account Status"
              value={statusFilter}
              onValueChange={(v) => v && setStatusFilter(v)}
              options={STATUS_OPTIONS}
              {...DOCK_SELECT_MENU_PORTAL}
            />
          </div>
          <div className="dock-users__filter-field dock-users__filter-field--select dock-users__filter-field--tags">
            <LabeledSelectInput
              name="tags"
              labelMode={LabelModes.column}
              label="Tags"
              isMulti
              clearable
              options={tagOptions}
              value={tagFilter}
              onMultiValuesChange={(v) => setTagFilter(v || [])}
              placeholder="Filter by tag"
              {...DOCK_SELECT_MENU_PORTAL}
            />
          </div>
        </div>
        <Button theme={ButtonThemes.blue} size={ButtonSizes.medium} onClick={onCreateEmployee}>
          Add Dock User
        </Button>
      </div>

      {isLoading && <Loading isLoading />}
      {isError && (
        <XGSErrorMessage>{(error as Error)?.message || "Unable to load employees."}</XGSErrorMessage>
      )}
      {!isLoading && !isError && (
        <DataTable
          columns={columns}
          data={enrichedEmployees}
          rowHeight={64}
          responsive
          stickyHeaders
          enableColumnResize
          enableColumnReorder
          showColumnSelector
          columnSelectorOptions={DOCK_EMPLOYEE_COLUMN_OPTIONS}
          columnVisibility={columnVisibility}
          onColumnVisibilityChange={setColumnVisibility}
          columnOrder={columnOrder}
          onColumnOrderChange={setColumnOrder}
          cursorPointer
          onRowClicked={(original) => onSelectEmployee(original as DockEmployee)}
          listHeightOverride={480}
          infiniteScroll
          infiniteScrollLoading={isFetchingNextPage}
          infiniteScrollHasNext={!!hasNextPage}
          onInfiniteScroll={() => fetchNextPage()}
        />
      )}
      {!isLoading && enrichedEmployees.length === 0 && !isError && (
        <div className="dock-users__empty">No dock users found for {terminalName}.</div>
      )}
    </div>
  );
};

export default DockEmployeesTab;
